import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const INTERVALS = [30, 60, 300];
const CODES = [200, 301, 302];
const DOT = { up: "bg-emerald-500", degraded: "bg-amber-500", down: "bg-red-500" };

export default function StatusSettings({ workspaceId }) {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    api.get("/status/servers", { params: { workspace_id: workspaceId } })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }, [workspaceId]);

  const patch = (i, field, value) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/status/servers",
        {
          servers: rows.map((r) => ({
            name: r.name,
            url: r.url,
            expected_status: Number(r.expected_status),
            check_interval: Number(r.check_interval),
          })),
        },
        { params: { workspace_id: workspaceId } });
      setRows(data);
      toast.success("Servere salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu s-a putut salva");
    } finally { setSaving(false); }
  };

  if (!rows) return null;

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="status-settings">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <Activity className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Status servere</h3>
        <span className="label-tiny ml-2">// MONITORIZARE</span>
        <button
          type="button" data-testid="status-add-server"
          onClick={() => setRows((prev) => [...prev, { id: `new-${prev.length}`, name: "", url: "", expected_status: 200, check_interval: 60, status: "unknown" }])}
          className="ml-auto text-xs flex items-center gap-1.5 px-2.5 py-1 border border-line/20 text-fg/60 hover:text-fg hover:border-line/40"
        ><Plus className="w-3.5 h-3.5" /> Adaugă</button>
      </div>

      <div className="p-5 space-y-3">
        <p className="text-xs text-fg-muted leading-relaxed">
          Se acceptă doar adrese publice — o adresă internă e refuzată, altfel serviciul de
          verificare ar deveni o sondă pentru tot ce e accesibil din rețeaua aplicației.
          Schimbările de stare ajung în canalul <span className="mono">#status</span>.
        </p>

        {rows.length === 0 && <p className="text-xs text-fg-muted">Niciun server configurat.</p>}

        {rows.map((r, i) => (
          <div key={r.id} className="flex flex-wrap items-center gap-2" data-testid={`status-row-${i}`}>
            <span
              className={`w-2 h-2 rounded-full flex-shrink-0 ${DOT[r.status] || "bg-fg/30"}`}
              title={r.status || "necunoscut"}
            />
            <input
              aria-label="Nume server" placeholder="Nume"
              value={r.name} onChange={(e) => patch(i, "name", e.target.value)}
              className="input-flat text-xs flex-1 min-w-[140px]"
            />
            <input
              aria-label="URL" placeholder="https://…"
              value={r.url} onChange={(e) => patch(i, "url", e.target.value)}
              className="input-flat text-xs mono flex-[2] min-w-[220px]"
            />
            <select
              aria-label="Cod aşteptat" value={r.expected_status}
              onChange={(e) => patch(i, "expected_status", e.target.value)}
              className="input-flat text-xs"
            >{CODES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            <select
              aria-label="Interval verificare" value={r.check_interval}
              onChange={(e) => patch(i, "check_interval", e.target.value)}
              className="input-flat text-xs"
            >{INTERVALS.map((s) => <option key={s} value={s}>{s}s</option>)}</select>
            <button
              type="button" data-testid={`status-remove-${i}`}
              onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
              aria-label={`Șterge ${r.name || "serverul"}`}
              className="text-fg/30 hover:text-destructive flex-shrink-0 p-1"
            ><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}

        <button
          type="button" data-testid="status-save" onClick={save} disabled={saving}
          className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
        ><Save className="w-4 h-4" /> {saving ? "Se salvează…" : "Salvează"}</button>
      </div>
    </section>
  );
}
